import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";
import { AVAILABLE_MODULES } from "../components/Access";

const Role = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const userId = useMemo(() => localStorage.getItem("userid"), []);

  const [roles, setRoles] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status] = useState("active"); // active | inactive | all
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // view | edit
  const [selectedRole, setSelectedRole] = useState(null);
  const [loadingRole, setLoadingRole] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [editAccess, setEditAccess] = useState([]);
  const [editIsActive, setEditIsActive] = useState(true);

  const getRoleId = (r) => r?.id || r?._id || r?.roleId;

  const isActiveParam = useMemo(() => {
    if (status === "all") return undefined;
    return status === "active";
  }, [status]);

  const fetchRoles = async () => {
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!userId) {
      toast.error("Missing userId. Please login again.");
      return;
    }

    let didIncrement = false;
    try {
      const payload = {
        action: "superadmin.getAllRoles",
        userId,
        page,
        limit,
        search,
        ...(typeof isActiveParam === "boolean" ? { isActive: isActiveParam } : {}),
      };

      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, payload);
      const data = response?.data;

      if (data?.status === "success") {
        setRoles(Array.isArray(data.roles) ? data.roles : []);
        setPagination(data.pagination || { page, limit, total: 0, pages: 1 });
      } else {
        toast.error(data?.message || "Failed to load roles");
        setRoles([]);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || "Something went wrong";
      toast.error(msg);
      setRoles([]);
    } finally {
      if (didIncrement) loaderDecrement();
    }
  };

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search, status]);

  // Debounced search (like your reference table UI)
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const applySearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedRole(null);
    setEditAccess([]);
    setEditIsActive(true);
    setLoadingRole(false);
    setSavingRole(false);
  };

  const openRoleModal = async (mode, roleRow) => {
    const roleId = getRoleId(roleRow);
    if (!roleId) {
      toast.error("Missing role id");
      return;
    }
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!userId) {
      toast.error("Missing userId. Please login again.");
      return;
    }

    setModalMode(mode);
    setModalOpen(true);
    setLoadingRole(true);
    setSelectedRole(null);

    let didIncrement = false;
    try {
      const payload = { action: "superadmin.getRoleById", userId, roleId };
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, payload);
      const data = response?.data;

      if (data?.status === "success") {
        const role = data?.role || data?.data?.role || data?.data || data?.result || null;
        setSelectedRole(role);
        const accessArr = Array.isArray(role?.access) ? role.access : [];
        setEditAccess(accessArr);
        setEditIsActive(role?.isActive !== false);
      } else {
        toast.error(data?.message || "Failed to load role");
        closeModal();
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || "Something went wrong";
      toast.error(msg);
      closeModal();
    } finally {
      if (didIncrement) loaderDecrement();
      setLoadingRole(false);
    }
  };

  const toggleEditModule = (moduleName) => {
    setEditAccess((prev) =>
      prev.includes(moduleName) ? prev.filter((m) => m !== moduleName) : [...prev, moduleName]
    );
  };

  const saveRole = async () => {
    const roleId = getRoleId(selectedRole);
    if (!roleId) {
      toast.error("Missing role id");
      return;
    }
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!userId) {
      toast.error("Missing userId. Please login again.");
      return;
    }
    if (!editAccess.length) {
      toast.error("Select at least one module access");
      return;
    }

    setSavingRole(true);
    let didIncrement = false;
    try {
      const payload = {
        action: "superadmin.updateRole",
        userId,
        roleId,
        access: editAccess,
        isActive: !!editIsActive,
      };

      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, payload);
      const data = response?.data;

      if (data?.status === "success") {
        toast.success(data?.message || "Role updated successfully");
        closeModal();
        fetchRoles();
      } else {
        toast.error(data?.message || "Failed to update role");
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || "Something went wrong";
      toast.error(msg);
    } finally {
      if (didIncrement) loaderDecrement();
      setSavingRole(false);
    }
  };

  const totalPages = Math.max(1, Number(pagination?.pages || 1));
  const currentPage = Math.min(Math.max(1, page), totalPages);

  const pageButtons = useMemo(() => {
    // Show up to 5 page numbers like common table UIs: (current-2 ... current+2)
    const windowSize = 5;
    const half = Math.floor(windowSize / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    const pages = [];
    for (let p = start; p <= end; p += 1) pages.push(p);
    return pages;
  }, [currentPage, totalPages]);

  return (
    <div className="employee-form">
      <div className="content-header">
        <div className="container-fluid d-flex justify-content-between align-items-end flex-wrap" style={{ gap: 12 }}>
          <div>
            <h1 className="m-0 txt_lft">Role</h1>
            <p style={{ margin: 0 }}>View all roles.</p>
          </div>
          <NavLink to="/role-add" className="btn btn-primary">
            Add Role
          </NavLink>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-body">
              <div className="role-table-box">
                <div className="role-table-top">
                  <form onSubmit={applySearch} className="role-table-search-wrap">
                    <input
                      type="text"
                      className="form-control role-table-search"
                      placeholder="Search employees..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </form>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover role-table">
                  <thead>
                    <tr>
                      <th style={{ width: 90 }}>
                        <span className="role-th">
                          Sl No.<i className="fa-solid fa-sort role-sort-icon" aria-hidden="true" />
                        </span>
                      </th>
                      <th>
                        <span className="role-th">
                          Name<i className="fa-solid fa-sort role-sort-icon" aria-hidden="true" />
                        </span>
                      </th>
                      <th>
                        <span className="role-th">
                          Access<i className="fa-solid fa-sort role-sort-icon" aria-hidden="true" />
                        </span>
                      </th>
                      <th style={{ width: 120 }}>
                        <span className="role-th">
                          Status<i className="fa-solid fa-sort role-sort-icon" aria-hidden="true" />
                        </span>
                      </th>
                      <th style={{ width: 160 }}>
                        <span className="role-th">Action</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {roles.length ? (
                      roles.map((r, idx) => (
                        <tr key={getRoleId(r) || idx}>
                          <td>{(pagination.page - 1) * pagination.limit + idx + 1}</td>
                          <td>{r?.name || "-"}</td>
                          <td>{Array.isArray(r?.access) ? r.access.join(", ") : "-"}</td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                padding: "0.35rem 0.65rem",
                                borderRadius: 999,
                                fontWeight: 600,
                                fontSize: 11,
                                background: r?.isActive ? "rgba(34,197,94,0.18)" : "rgba(239,68,68,0.18)",
                                color: r?.isActive ? "#16a34a" : "#dc2626",
                              }}
                            >
                              {r?.isActive ? "ACTIVE" : "INACTIVE"}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex" style={{ gap: 8 }}>
                              <button
                                type="button"
                                className="role-action-btn role-action-view"
                                title="View"
                                onClick={() => openRoleModal("view", r)}
                              >
                                <i className="fa-solid fa-eye" aria-hidden="true" />
                              </button>
                              <button
                                type="button"
                                className="role-action-btn role-action-edit"
                                title="Edit"
                                onClick={() => openRoleModal("edit", r)}
                              >
                                <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
                              </button>
                              <button
                                type="button"
                                className="role-action-btn role-action-delete"
                                title="Delete"
                                onClick={() => toast.info("Delete role - coming soon")}
                              >
                                <i className="fa-solid fa-trash" aria-hidden="true" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center text-muted">
                          No roles found
                        </td>
                      </tr>
                    )}
                  </tbody>
                  </table>
                </div>

                <div className="role-table-footer d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 10 }}>
                  <div className="d-flex align-items-center" style={{ gap: 10 }}>
                    <span className="text-muted" style={{ fontSize: 12 }}>Show:</span>
                    <select
                      className="form-control"
                      style={{ width: 90 }}
                      value={limit}
                      onChange={(e) => {
                        setPage(1);
                        setLimit(Number(e.target.value));
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>

                  <div className="btn-group">
                    <button type="button" className="btn btn-light" disabled={currentPage <= 1} onClick={() => setPage(1)}>
                      &laquo;
                    </button>
                    <button
                      type="button"
                      className="btn btn-light"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      &lsaquo;
                    </button>

                    {pageButtons.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`btn ${p === currentPage ? "btn-primary" : "btn-light"}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    ))}

                    <button
                      type="button"
                      className="btn btn-light"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      &rsaquo;
                    </button>
                    <button
                      type="button"
                      className="btn btn-light"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage(totalPages)}
                    >
                      &raquo;
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {modalOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={closeModal}
        >
          <div
            className="card app-modal-card"
            style={{ width: "min(760px, 96vw)", maxHeight: "90vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header app-modal-header">
              <div className="app-modal-title">{modalMode === "edit" ? "Edit Role" : "View Role"}</div>
              <button
                type="button"
                className="app-modal-close"
                onClick={closeModal}
                disabled={savingRole}
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>

            <div className="card-body">
              {loadingRole ? (
                <div className="text-center text-muted" style={{ padding: 24 }}>
                  Loading...
                </div>
              ) : selectedRole ? (
                <>
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <div className="form-group">
                        <label className="form-label">Role Name</label>
                        <input type="text" className="form-control" value={selectedRole?.name || ""} disabled />
                        <small className="text-muted d-block mt-1">Role name cannot be updated after creation.</small>
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="form-group">
                        <label className="form-label">Status</label>
                        {modalMode === "edit" ? (
                          <select
                            className="form-control"
                            value={editIsActive ? "active" : "inactive"}
                            onChange={(e) => setEditIsActive(e.target.value === "active")}
                            disabled={savingRole}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        ) : (
                          <input
                            type="text"
                            className="form-control"
                            value={selectedRole?.isActive ? "Active" : "Inactive"}
                            disabled
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-2">
                    <label className="form-label">Access</label>
                    {modalMode === "view" ? (
                      <div className="text-muted" style={{ fontSize: 13 }}>
                        {Array.isArray(selectedRole?.access) && selectedRole.access.length
                          ? selectedRole.access.join(", ")
                          : "-"}
                      </div>
                    ) : (
                      <div className="row">
                        {AVAILABLE_MODULES.map((m) => (
                          <div className="col-12 col-md-4 col-lg-3" key={m} style={{ marginBottom: 10 }}>
                            <div className="custom-control custom-checkbox">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id={`edit-access-${m}`}
                                checked={editAccess.includes(m)}
                                onChange={() => toggleEditModule(m)}
                                disabled={savingRole}
                              />
                              <label className="custom-control-label" htmlFor={`edit-access-${m}`}>
                                {m}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center text-muted" style={{ padding: 24 }}>
                  No data
                </div>
              )}
            </div>

            {modalMode === "edit" ? (
              <div className="card-footer d-flex justify-content-end" style={{ gap: 10 }}>
                <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={savingRole}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={saveRole} disabled={savingRole || loadingRole}>
                  {savingRole ? "Saving..." : "Save"}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Role;